import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Clock, ArrowRight, ArrowLeft, AlertCircle, X, Sparkles, Home, Volume2, VolumeX } from 'lucide-react';
import useLocalStorage from './hooks/useLocalStorage';

const MAX_TASK_LENGTH = 200;
const MAX_HOURS = 12;
const MAX_SESSION_MINUTES = 180;

const sanitize = (str) => String(str ?? '').replace(/[<>"'`]/g, '').slice(0, MAX_TASK_LENGTH);

const DEFAULT_PREFS = {
  hours: 4,
  split: { work: 25, break: 5, longBreak: 15 },
  sessionsUntilLongBreak: 4,
  soundEnabled: true,
  isCustom: false,
  customWork: 25,
  customBreak: 5,
  customLongBreak: 15,
};

const TIPS = {
  setup: "Pro tip: Most people overestimate what they can do in a day. Start conservative!",
  plan: [
    "Tackle the hardest task first while your brain is fresh!",
    "Break big tasks into smaller chunks—one session, one focused goal.",
    "Don't pack every session full. Leave room to breathe.",
    "Stack similar tasks together to stay in the flow.",
  ],
};

// ── PomodoroPlanner ───────────────────────────────────────────

const PomodoroPlanner = ({ setCurrentModule }) => {
  const [prefs, setPrefs] = useLocalStorage('pomodoroPreferences', DEFAULT_PREFS);

  const hours                  = prefs.hours;
  const split                  = prefs.split;
  const sessionsUntilLongBreak = prefs.sessionsUntilLongBreak;
  const soundEnabled           = prefs.soundEnabled;
  const isCustom               = prefs.isCustom;
  const customWork             = prefs.customWork;
  const customBreak            = prefs.customBreak;
  const customLongBreak        = prefs.customLongBreak;

  const setHours                  = (v) => setPrefs(p => ({ ...p, hours: Math.min(Math.max(1, v), MAX_HOURS) }));
  const setSplit                  = (v) => setPrefs(p => ({ ...p, split: v }));
  const setSessionsUntilLongBreak = (v) => setPrefs(p => ({ ...p, sessionsUntilLongBreak: Math.min(Math.max(2, v), 8) }));
  const setSoundEnabled           = (v) => setPrefs(p => ({ ...p, soundEnabled: v }));
  const setIsCustom               = (v) => setPrefs(p => ({ ...p, isCustom: v }));
  const setCustomWork             = (v) => setPrefs(p => ({ ...p, customWork: Math.min(Math.max(1, v), MAX_SESSION_MINUTES) }));
  const setCustomBreak            = (v) => setPrefs(p => ({ ...p, customBreak: Math.min(Math.max(1, v), MAX_SESSION_MINUTES) }));
  const setCustomLongBreak        = (v) => setPrefs(p => ({ ...p, customLongBreak: Math.min(Math.max(1, v), MAX_SESSION_MINUTES) }));

  const [step,           setStep]           = useState('setup');
  const [sessions,       setSessions]       = useState([]);
  const [currentSession, setCurrentSession] = useState(0);
  const [timeLeft,       setTimeLeft]       = useState(0);
  const [isRunning,      setIsRunning]      = useState(false);
  const [isBreak,        setIsBreak]        = useState(false);
  const [draggedTask,    setDraggedTask]    = useState(null);

  const timerRef       = useRef(null);
  const audioContextRef = useRef(null);
  const planTipRef     = useRef(TIPS.plan[Math.floor(Math.random() * TIPS.plan.length)]);

  // Audio
  useEffect(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) audioContextRef.current = new Ctx();
    } catch {}
  }, []);

  const playSound = (type) => {
    if (!soundEnabled || !audioContextRef.current) return;
    try {
      const ctx = audioContextRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);

      if (type === 'work-end') {
        osc.frequency.setValueAtTime(523.25, ctx.currentTime);
        osc.frequency.setValueAtTime(659.25, ctx.currentTime + 0.1);
        osc.frequency.setValueAtTime(783.99, ctx.currentTime + 0.2);
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
        osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.5);
      } else if (type === 'break-end') {
        osc.frequency.setValueAtTime(440, ctx.currentTime);
        osc.frequency.setValueAtTime(554.37, ctx.currentTime + 0.15);
        gain.gain.setValueAtTime(0.25, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
        osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.4);
      } else if (type === 'complete') {
        [523.25, 587.33, 659.25, 698.46, 783.99].forEach((freq, i) => {
          const o = ctx.createOscillator(), g = ctx.createGain();
          o.connect(g); g.connect(ctx.destination);
          o.frequency.setValueAtTime(freq, ctx.currentTime);
          g.gain.setValueAtTime(0.2, ctx.currentTime + i * 0.1);
          g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.1 + 0.3);
          o.start(ctx.currentTime + i * 0.1); o.stop(ctx.currentTime + i * 0.1 + 0.3);
        });
      }
    } catch {}
  };

  // Timer
  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      timerRef.current = setInterval(() => setTimeLeft(t => t - 1), 1000);
    } else if (timeLeft === 0 && isRunning) {
      setIsRunning(false);
      if (!isBreak) {
        playSound('work-end');
        const updated = [...sessions];
        if (updated[currentSession]) updated[currentSession].status = 'completed';
        setSessions(updated);

        const completions = JSON.parse(localStorage.getItem('pomodoroCompletions') || '[]');
        completions.push({ timestamp: new Date().toISOString(), session: currentSession + 1, duration: split.work });
        localStorage.setItem('pomodoroCompletions', JSON.stringify(completions));

        if (currentSession < sessions.length - 1) {
          setIsBreak(true);
          const isLong = (currentSession + 1) % sessionsUntilLongBreak === 0;
          setTimeLeft(isLong ? split.longBreak * 60 : split.break * 60);
        } else {
          playSound('complete');
        }
      } else {
        playSound('break-end');
        setIsBreak(false);
        setCurrentSession(c => c + 1);
        if (currentSession + 1 < sessions.length) setTimeLeft(split.work * 60);
      }
    }
    return () => clearInterval(timerRef.current);
  }, [isRunning, timeLeft, isBreak, currentSession, sessions, split, sessionsUntilLongBreak]);

  // Session helpers
  const generateSessions = () => {
    const cycleTime = split.work + split.break;
    const num = Math.max(1, Math.floor((hours * 60) / cycleTime));
    setSessions(Array.from({ length: num }, (_, i) => ({ id: i, title: `Session ${i + 1}`, tasks: [], status: 'pending' })));
    setCurrentSession(0);
    setStep('plan');
  };

  const startTimer = () => {
    if (timeLeft === 0) {
      setTimeLeft(split.work * 60);
      const updated = [...sessions];
      if (updated[currentSession]) updated[currentSession].status = 'active';
      setSessions(updated);
    }
    setIsRunning(true);
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Task helpers
  const addTask = (sessionId, raw) => {
    const text = sanitize(raw);
    if (!text.trim()) return;
    const updated = [...sessions];
    const session = updated.find(s => s.id === sessionId);
    if (!session) return;
    session.tasks.push({ id: Date.now(), text, done: false });
    setSessions(updated);
  };

  const deleteTask = (sessionId, taskId) => {
    const updated = [...sessions];
    const session = updated.find(s => s.id === sessionId);
    if (!session) return;
    session.tasks = session.tasks.filter(t => t.id !== taskId);
    setSessions(updated);
  };

  const toggleTask = (sessionId, taskId) => {
    const updated = [...sessions];
    const session = updated.find(s => s.id === sessionId);
    if (!session) return;
    const task = session.tasks.find(t => t.id === taskId);
    if (task) task.done = !task.done;
    setSessions(updated);
  };

  const moveTask = (sessionId, taskId, dir) => {
    const updated = [...sessions];
    const src = updated.find(s => s.id === sessionId);
    if (!src) return;
    const idx = src.tasks.findIndex(t => t.id === taskId);
    if (idx === -1) return;
    const [task] = src.tasks.splice(idx, 1);
    const targetIdx = sessionId + dir;
    if (targetIdx < 0 || targetIdx >= updated.length) { src.tasks.splice(idx, 0, task); return; }
    updated[targetIdx].tasks.push(task);
    setSessions(updated);
  };

  const handleDragStart = (sessionId, taskId) => setDraggedTask({ sessionId, taskId });

  const handleDrop = (targetSessionId) => {
    if (!draggedTask) return;
    const updated = [...sessions];
    const src = updated.find(s => s.id === draggedTask.sessionId);
    const tgt = updated.find(s => s.id === targetSessionId);
    if (!src || !tgt) { setDraggedTask(null); return; }
    const idx = src.tasks.findIndex(t => t.id === draggedTask.taskId);
    if (idx < 0) { setDraggedTask(null); return; }
    const [task] = src.tasks.splice(idx, 1);
    tgt.tasks.push(task);
    setSessions(updated);
    setDraggedTask(null);
  };

  // ── Setup step ────────────────────────────────────────────────

  if (step === 'setup') {
    return (
      <div className="page p-4 sm:p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <button onClick={() => setCurrentModule('dashboard')} className="btn-back" aria-label="Back to Dashboard">
            <Home className="w-4 h-4" /><span>Dashboard</span>
          </button>

          <div className="max-w-xl mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="page-title">POMODORO</h1>
              <Sparkles className="w-6 h-6 text-neutral-900" />
            </div>
            <p className="page-subtitle mb-6">Plan focused work sessions</p>

            <div className="tip-banner mb-8">
              <AlertCircle className="w-5 h-5 text-neutral-900 flex-shrink-0 mt-0.5" />
              <p className="text-neutral-900 text-sm font-mono">{TIPS.setup}</p>
            </div>

            <div className="space-y-8">

              {/* Hours */}
              <div className="card-md bg-white p-6">
                <label className="label mb-3">Hours to work</label>
                <input
                  inputMode="numeric" type="number" min="1" max={MAX_HOURS}
                  value={hours}
                  onChange={e => setHours(parseInt(e.target.value) || 1)}
                  className="w-full border-b-4 border-neutral-900 bg-transparent px-0 py-2 text-4xl sm:text-5xl md:text-6xl font-bold text-neutral-900 focus:outline-none font-mono"
                />
              </div>

              {/* Split */}
              <div className="card-md bg-white p-6">
                <label className="label mb-4">Work/Break Split</label>
                <div className="space-y-3">
                  {[
                    { work: 25, break: 5, longBreak: 15, label: 'Classic Pomodoro' },
                    { work: 50, break: 10, longBreak: 30, label: 'Extended Focus' },
                    { label: 'Custom' },
                  ].map(s => (
                    <button
                      key={s.label}
                      onClick={() => {
                        if (s.label === 'Custom') { setIsCustom(true); setSplit({ work: customWork, break: customBreak, longBreak: customLongBreak }); }
                        else { setIsCustom(false); setSplit(s); }
                      }}
                      className={`w-full text-left px-4 py-4 border-2 border-neutral-900 transition-all hover:-translate-x-0.5 hover:-translate-y-0.5 ${
                        (s.label === 'Custom' ? isCustom : split.work === s.work && split.break === s.break && !isCustom)
                          ? 'bg-neutral-900 text-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
                          : 'bg-white hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-bold font-mono uppercase tracking-wide">{s.label}</span>
                        <span className="text-sm font-mono">{s.work ?? customWork}/{s.break ?? customBreak}/{s.longBreak ?? customLongBreak}min</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom inputs */}
              {isCustom && (
                <div className="space-y-3 p-4 border-2 border-neutral-900 bg-neutral-50">
                  {[
                    { label: 'Work minutes',        val: customWork,      set: setCustomWork },
                    { label: 'Short break minutes', val: customBreak,     set: setCustomBreak },
                    { label: 'Long break minutes',  val: customLongBreak, set: setCustomLongBreak },
                  ].map(({ label, val, set }) => (
                    <div key={label}>
                      <label className="block text-xs uppercase tracking-wider font-bold font-mono text-neutral-700 mb-1">{label}</label>
                      <input
                        type="number" min="1" max={MAX_SESSION_MINUTES} value={val}
                        onChange={e => {
                          const v = Math.min(Math.max(1, parseInt(e.target.value) || 1), MAX_SESSION_MINUTES);
                          set(v);
                          setSplit({ work: label.includes('Work') ? v : customWork, break: label.includes('Short') ? v : customBreak, longBreak: label.includes('Long') ? v : customLongBreak });
                        }}
                        className="w-full border-2 border-neutral-900 px-3 py-2 text-lg font-bold font-mono focus:outline-none"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Sessions until long break */}
              <div className="card-md bg-white p-6">
                <label className="label mb-3">Sessions until long break</label>
                <input
                  inputMode="numeric" type="number" min="2" max="8"
                  value={sessionsUntilLongBreak}
                  onChange={e => setSessionsUntilLongBreak(parseInt(e.target.value) || 4)}
                  className="w-full border-b-4 border-neutral-900 bg-transparent px-0 py-2 text-3xl sm:text-4xl md:text-5xl font-bold text-neutral-900 focus:outline-none font-mono"
                />
                <p className="text-neutral-600 text-sm mt-3 font-mono">
                  {split.longBreak}min break after every {sessionsUntilLongBreak} sessions
                </p>
              </div>

              {/* Sound */}
              <div className="card-md bg-white p-6">
                <label className="label mb-3">Sound Notifications</label>
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className={`w-full flex items-center justify-between px-4 py-4 border-2 border-neutral-900 transition-all hover:-translate-x-0.5 hover:-translate-y-0.5 ${
                    soundEnabled ? 'bg-neutral-900 text-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]' : 'bg-white hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
                  }`}
                >
                  <span className="font-bold font-mono uppercase tracking-wide">{soundEnabled ? 'Enabled' : 'Disabled'}</span>
                  {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                </button>
                <p className="text-neutral-600 text-sm mt-3 font-mono">Notification sounds when sessions end</p>
              </div>

              <button
                onClick={generateSessions}
                className="w-full bg-neutral-900 text-white py-4 sm:py-5 text-sm uppercase tracking-wider font-bold border-2 border-neutral-900 hover:bg-neutral-700 transition-all flex items-center justify-center gap-2 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover-lift-xl font-mono"
              >
                Generate Sessions <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Plan step ─────────────────────────────────────────────────

  if (step === 'plan') {
    return (
      <div className="page p-4 sm:p-6 md:p-8">
        <div className="max-w-7xl mx-auto">
          <button onClick={() => setCurrentModule('dashboard')} className="btn-back">
            <Home className="w-4 h-4" /><span>Dashboard</span>
          </button>

          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-6 border-b-4 border-neutral-900 pb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif text-neutral-900 mb-1">SESSION PLANNING</h1>
              <p className="text-neutral-700 text-sm font-mono font-bold uppercase">{sessions.length} sessions · {hours}h total</p>
            </div>
            <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button onClick={() => setStep('setup')} className="border-2 border-neutral-900 bg-white text-neutral-900 px-4 py-2 text-sm uppercase tracking-wider font-bold hover-lift font-mono">
                ← Setup
              </button>
              <button onClick={() => setStep('work')} className="bg-neutral-900 text-white px-4 py-2 text-sm uppercase tracking-wider font-bold border-2 border-neutral-900 hover:bg-neutral-700 transition-all flex items-center gap-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg font-mono">
                Begin <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="tip-banner mb-6">
            <AlertCircle className="w-5 h-5 text-neutral-900 flex-shrink-0 mt-0.5" />
            <p className="text-neutral-900 text-sm font-mono">{planTipRef.current}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sessions.map(session => (
              <div
                key={session.id}
                onDragOver={e => e.preventDefault()}
                onDrop={() => handleDrop(session.id)}
                className="border-2 border-neutral-900 bg-white hover-lift-lg overflow-hidden"
              >
                <div className="border-b-2 border-neutral-900 px-3 sm:px-4 py-3 bg-neutral-900 flex items-center justify-between">
                  <h3 className="font-bold text-white text-sm font-mono uppercase tracking-wide">Session {session.id + 1}</h3>
                  <Clock className="w-4 h-4 text-white" />
                </div>

                <div className="p-3 sm:p-4">
                  <div className="space-y-2 mb-3 min-h-[100px] sm:min-h-[120px]">
                    {session.tasks.map(task => (
                      <div
                        key={task.id}
                        draggable
                        onDragStart={() => handleDragStart(session.id, task.id)}
                        className="border-2 border-neutral-900 p-2 sm:p-3 cursor-move hover-lift group bg-white flex items-center justify-between gap-2"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-neutral-900 truncate font-mono">{task.text}</div>
                          <div className="mt-2 flex gap-2">
                            <button onClick={() => moveTask(session.id, task.id, -1)} className="inline-flex items-center justify-center p-1 border-2 border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors" aria-label="Move to previous session">
                              <ArrowLeft className="w-3 h-3" />
                            </button>
                            <button onClick={() => moveTask(session.id, task.id, 1)} className="inline-flex items-center justify-center p-1 border-2 border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors" aria-label="Move to next session">
                              <ArrowRight className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <button onClick={() => deleteTask(session.id, task.id)} className="text-neutral-900 hover:bg-red-500 hover:text-white border-2 border-neutral-900 p-1 transition-colors" aria-label="Delete task">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <input
                    type="text"
                    maxLength={MAX_TASK_LENGTH}
                    placeholder="Add task..."
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        addTask(session.id, e.currentTarget.value);
                        e.currentTarget.value = '';
                      }
                    }}
                    className="w-full border-b-2 border-neutral-900 bg-transparent px-0 py-2 text-sm text-neutral-900 placeholder-neutral-500 focus:outline-none focus:border-b-4 transition-all font-mono"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Work step ─────────────────────────────────────────────────

  const currentSessionData  = sessions[currentSession];
  const progress            = sessions.length > 0 ? ((currentSession + 1) / sessions.length) * 100 : 0;
  const isLongBreak         = isBreak && (currentSession + 1) % sessionsUntilLongBreak === 0;
  const allSessionsComplete = sessions.length === 0 || currentSession >= sessions.length;

  return (
    <div className="page p-4 sm:p-6 md:p-8">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => setCurrentModule('dashboard')} className="btn-back">
          <Home className="w-4 h-4" /><span>Dashboard</span>
        </button>

        <div className="max-w-2xl mx-auto">
          {allSessionsComplete ? (
            <div className="card bg-white p-8 sm:p-12 text-center">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif text-neutral-900 mb-3 uppercase">All Done!</h2>
              <p className="text-neutral-700 mb-6 text-base font-mono">Completed {sessions.length} sessions</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => { setStep('setup'); setCurrentSession(0); setIsBreak(false); setTimeLeft(0); setIsRunning(false); }}
                  className="bg-neutral-900 text-white px-6 py-3 text-sm uppercase tracking-wider font-bold border-2 border-neutral-900 hover:bg-neutral-700 transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg font-mono"
                >
                  Start New
                </button>
                <button onClick={() => setCurrentModule('dashboard')} className="border-2 border-neutral-900 bg-white text-neutral-900 px-6 py-3 text-sm uppercase tracking-wider font-bold hover-lift font-mono">
                  Dashboard
                </button>
              </div>
            </div>
          ) : (
            <div className="card bg-white overflow-hidden">
              <div className="border-b-2 border-neutral-900 px-4 py-4 bg-neutral-900">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h2 className="text-xl sm:text-2xl font-serif text-white mb-1 uppercase">
                      {isBreak ? (isLongBreak ? '🎉 Long Break' : '☕ Break') : `Session ${currentSession + 1}`}
                    </h2>
                    <p className="text-neutral-300 text-sm font-mono">
                      {isBreak && isLongBreak && `${split.longBreak} minutes · `}{currentSession + 1} of {sessions.length}
                    </p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <button onClick={() => setSoundEnabled(!soundEnabled)} className="text-white hover:bg-neutral-700 transition-colors p-2 border-2 border-white" title={soundEnabled ? 'Mute' : 'Enable sound'}>
                      {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                    </button>
                    <button onClick={() => setStep('setup')} className="text-white hover:bg-neutral-700 text-sm uppercase tracking-wider transition-colors px-2 py-1 border-2 border-white font-mono font-bold">Setup</button>
                    <button onClick={() => setStep('plan')}  className="text-white hover:bg-neutral-700 text-sm uppercase tracking-wider transition-colors px-2 py-1 border-2 border-white font-mono font-bold">Plan</button>
                  </div>
                </div>
                <div className="bg-neutral-700 h-2 overflow-hidden border-2 border-white">
                  <div className="bg-white h-full transition-all duration-500 ease-out" style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
                </div>
              </div>

              <div className="p-6 sm:p-8 text-center">
                {isBreak && (
                  <div className="tip-banner mb-4 text-left">
                    <AlertCircle className="w-5 h-5 text-neutral-900 flex-shrink-0 mt-0.5" />
                    <p className="text-neutral-900 text-sm font-mono">
                      {isLongBreak ? "Nice work! Take a real break—step away from the screen, stretch, hydrate." : "Quick break! Stand up, look away from the screen, take a breath."}
                    </p>
                  </div>
                )}

                <div className="text-6xl sm:text-8xl md:text-9xl font-bold text-neutral-900 mb-6 font-mono tracking-tighter transition-all duration-300">
                  {formatTime(timeLeft)}
                </div>

                <div className="flex justify-center gap-4 mb-6">
                  <button
                    onClick={() => isRunning ? setIsRunning(false) : startTimer()}
                    className="border-2 border-neutral-900 bg-white text-neutral-900 p-4 sm:p-5 hover:bg-neutral-900 hover:text-white transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg"
                    aria-label={isRunning ? 'Pause timer' : 'Start timer'}
                  >
                    {isRunning ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                  </button>
                  <button
                    onClick={() => { setIsRunning(false); setTimeLeft(isBreak ? (isLongBreak ? split.longBreak * 60 : split.break * 60) : split.work * 60); }}
                    className="border-2 border-neutral-900 bg-white text-neutral-900 p-4 sm:p-5 hover:bg-neutral-900 hover:text-white transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg"
                    aria-label="Reset timer"
                  >
                    <RotateCcw className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {!isBreak && currentSessionData && (
                <div className="border-t-2 border-neutral-900 px-4 py-4 sm:px-8 sm:py-6 bg-neutral-50">
                  <h3 className="text-sm uppercase tracking-wider text-neutral-900 mb-3 font-bold font-mono">Tasks</h3>
                  <div className="space-y-2">
                    {currentSessionData.tasks.length === 0 ? (
                      <p className="text-neutral-500 text-center py-4 text-sm font-mono">No tasks for this session</p>
                    ) : currentSessionData.tasks.map(task => (
                      <div key={task.id} className="border-2 border-neutral-900 p-3 flex items-start gap-3 hover-lift group bg-white">
                        <button onClick={() => toggleTask(currentSession, task.id)} className="flex-shrink-0 mt-0.5" aria-label="Toggle task">
                          <div className={`w-6 h-6 border-2 transition-all flex items-center justify-center ${task.done ? 'border-neutral-900 bg-neutral-900' : 'border-neutral-900'}`}>
                            {task.done && <svg className="w-full h-full text-white" viewBox="0 0 16 16" fill="none"><path d="M3 8L6 11L13 4" stroke="currentColor" strokeWidth="3" /></svg>}
                          </div>
                        </button>
                        <span className={`text-neutral-900 flex-1 font-mono ${task.done ? 'line-through text-neutral-400' : ''}`}>{task.text}</span>
                        <div className="flex items-center gap-2">
                          <button onClick={() => moveTask(currentSession, task.id, -1)} className="p-1 border-2 border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors" aria-label="Move to previous session"><ArrowLeft className="w-4 h-4" /></button>
                          <button onClick={() => moveTask(currentSession, task.id, 1)}  className="p-1 border-2 border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors" aria-label="Move to next session"><ArrowRight className="w-4 h-4" /></button>
                          <button onClick={() => deleteTask(currentSession, task.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-white bg-red-600 hover:bg-red-700 border-2 border-neutral-900 p-1" aria-label="Delete task"><X className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PomodoroPlanner;